import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FAQS } from './components/FaqSection';
import { GUIA_FAQS, GUIA_PASOS, GUIA_UPDATED } from './pages/GuiaVucemPage';
import { MVE_FAQS, MVE_PASOS, MVE_UPDATED } from './pages/MveGuidePage';
import { EDOC_MVE_FAQS, EDOC_MVE_PASOS, EDOC_MVE_UPDATED } from './pages/EdocumentMvePage';
import { PREP_MVE_FAQS, PREP_MVE_PASOS, PREP_MVE_UPDATED } from './pages/PrepararseMvePage';

// Public origin of the marketing site (no trailing slash). Used for canonical
// URLs, Open Graph tags and structured data. Keep public/robots.txt,
// public/sitemap.xml and public/llms.txt in sync if this changes.
export const SITE_URL = 'https://eccompliance.com';
export const SITE_NAME = 'EC-Compliance';
const OG_IMAGE = `${SITE_URL}/foreign-trade-hero.jpg`;

const ORGANIZATION = {
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#organizacion`,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/favicon.png`,
  image: OG_IMAGE,
  description:
    'Consultoría y software de comercio exterior en México: pedimentos, expediente digital VUCEM, COVE, manifestación de valor, Anexo 24 y 30, control de bodega y logística internacional.',
  areaServed: { '@type': 'Country', name: 'México' },
  telephone: '+52 442 142 4827',
  email: 'info@eccompliance.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Del Mesón 184, Col. Del Prado',
    postalCode: '76030',
    addressLocality: 'Santiago de Querétaro',
    addressRegion: 'Qro.',
    addressCountry: 'MX',
  },
  geo: { '@type': 'GeoCoordinates', latitude: 20.583247, longitude: -100.401862 },
  hasMap: 'https://www.google.com/maps?q=20.583247022675213,-100.40186154631137',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+52 442 142 4827',
    email: 'info@eccompliance.com',
    contactType: 'customer service',
    areaServed: 'MX',
    availableLanguage: 'es',
  },
  knowsAbout: [
    'Comercio exterior',
    'VUCEM',
    'VUTCE',
    'Manifestación de Valor Electrónica (MVE)',
    'Pedimentos aduanales',
    'COVE',
    'Manifestación de valor',
    'Anexo 24',
    'Anexo 30',
    'IMMEX',
    'Expediente digital aduanero',
  ],
};

const faqPage = (faqs) => ({
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

// Structured data shared by the guide pages (ArticleLayout): Article, HowTo,
// FAQPage and BreadcrumbList, built from the same arrays the page renders.
const guideGraph = ({ path, headline, updated, steps, faqs, totalTime, tool }) => {
  const url = `${SITE_URL}${path}`;
  return [
    {
      '@type': 'Article',
      headline,
      url,
      inLanguage: 'es-MX',
      datePublished: updated,
      dateModified: updated,
      image: OG_IMAGE,
      author: { '@id': ORGANIZATION['@id'] },
      publisher: { '@id': ORGANIZATION['@id'] },
      mainEntityOfPage: url,
    },
    {
      '@type': 'HowTo',
      name: headline,
      ...(totalTime && { totalTime }),
      ...(tool && { tool }),
      step: steps.map(({ title, description }, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: title,
        text: description,
      })),
    },
    faqPage(faqs),
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: headline, item: url },
      ],
    },
    ORGANIZATION,
  ];
};

// Per-route metadata. The prerender script (scripts/prerender.js) writes these
// into the static HTML for crawlers; usePageMeta keeps them in sync during
// client-side navigation.
export const PAGES = {
  '/': {
    title: 'EC-Compliance | Soluciones Integrales en Comercio Exterior',
    description:
      'Software y asesoría en comercio exterior para México: pedimentos y expediente digital, transmisión a VUCEM, COVE, manifestación de valor, Anexo 24 y 30, y control de bodega.',
    jsonLd: () => [
      ORGANIZATION,
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#sitio`,
        url: `${SITE_URL}/`,
        name: SITE_NAME,
        inLanguage: 'es-MX',
        publisher: { '@id': ORGANIZATION['@id'] },
      },
      faqPage(FAQS),
    ],
  },
  '/convertidor': {
    title: 'Convertidor PDF VUCEM gratis (300 DPI, escala de grises) | EC-Compliance',
    description:
      'Convierte tus PDF al formato que exige VUCEM: 300 DPI y escala de grises. Gratis, en línea y sin guardar tus archivos.',
    jsonLd: () => [
      {
        '@type': 'WebApplication',
        name: 'Convertidor PDF VUCEM',
        url: `${SITE_URL}/convertidor`,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        inLanguage: 'es-MX',
        isAccessibleForFree: true,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'MXN' },
        description:
          'Herramienta en línea que convierte archivos PDF al formato requerido por la Ventanilla Única de Comercio Exterior Mexicana (VUCEM): 300 DPI y escala de grises.',
        provider: { '@id': ORGANIZATION['@id'] },
      },
    ],
  },
  '/como-convertir-pdf-vucem': {
    title: 'Cómo convertir un PDF al formato VUCEM | EC-Compliance',
    description:
      'Requisitos de VUCEM para digitalizar documentos y cómo convertir tu PDF a 300 DPI y escala de grises paso a paso, gratis y en línea.',
    jsonLd: () =>
      guideGraph({
        path: '/como-convertir-pdf-vucem',
        headline: 'Cómo convertir un PDF al formato VUCEM',
        updated: GUIA_UPDATED,
        steps: GUIA_PASOS,
        totalTime: 'PT2M',
        tool: { '@type': 'HowToTool', name: 'Convertidor PDF VUCEM', url: `${SITE_URL}/convertidor` },
        faqs: GUIA_FAQS,
      }),
  },
  '/que-es-manifestacion-de-valor-electronica': {
    title: '¿Qué es la Manifestación de Valor Electrónica (MVE)?',
    description:
      'Qué es la MVE, desde cuándo es obligatoria según el calendario vigente, quién la firma, qué información y documentos lleva, y cómo se presenta en la VUTCE (antes VUCEM) paso a paso.',
    jsonLd: () =>
      guideGraph({
        path: '/que-es-manifestacion-de-valor-electronica',
        headline: '¿Qué es la Manifestación de Valor Electrónica (MVE)?',
        updated: MVE_UPDATED,
        steps: MVE_PASOS,
        faqs: MVE_FAQS,
      }),
  },
  '/como-digitalizar-edocument-manifestacion-de-valor': {
    title: 'Cómo digitalizar un e-document para la MVE | EC-Compliance',
    description:
      'Requisitos del PDF y paso a paso para digitalizar y adjuntar documentos soporte a la Manifestación de Valor Electrónica (MVE) en la ventanilla única, según el manual oficial.',
    jsonLd: () =>
      guideGraph({
        path: '/como-digitalizar-edocument-manifestacion-de-valor',
        headline: '¿Cómo digitalizo un e-document para la Manifestación de Valor Electrónica (MVE)?',
        updated: EDOC_MVE_UPDATED,
        steps: EDOC_MVE_PASOS,
        tool: { '@type': 'HowToTool', name: 'Convertidor PDF VUCEM', url: `${SITE_URL}/convertidor` },
        faqs: EDOC_MVE_FAQS,
      }),
  },
  '/como-prepararse-para-la-mve': {
    title: 'Cómo prepararte para la MVE y qué sistema usar | EC-Compliance',
    description:
      'Lista para prepararte para la Manifestación de Valor Electrónica (MVE): e.firma, expediente de valor, valoración, proceso con tu agente aduanal y comparación de sistemas para presentarla.',
    jsonLd: () =>
      guideGraph({
        path: '/como-prepararse-para-la-mve',
        headline: '¿Cómo me preparo para la MVE y qué sistema puedo usar?',
        updated: PREP_MVE_UPDATED,
        steps: PREP_MVE_PASOS,
        faqs: PREP_MVE_FAQS,
      }),
  },
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Builds the <head> tags for a route as an HTML string. Used at build time by
// scripts/prerender.js.
export const renderHeadTags = (path) => {
  const page = PAGES[path] || PAGES['/'];
  const url = `${SITE_URL}${path === '/' ? '/' : path}`;
  const jsonLd = JSON.stringify({ '@context': 'https://schema.org', '@graph': page.jsonLd() }).replace(/</g, '\\u003c');

  return [
    `<title>${escapeHtml(page.title)}</title>`,
    `<meta name="description" content="${escapeHtml(page.description)}">`,
    `<link rel="canonical" href="${url}">`,
    `<meta property="og:type" content="website">`,
    `<meta property="og:locale" content="es_MX">`,
    `<meta property="og:site_name" content="${SITE_NAME}">`,
    `<meta property="og:title" content="${escapeHtml(page.title)}">`,
    `<meta property="og:description" content="${escapeHtml(page.description)}">`,
    `<meta property="og:url" content="${url}">`,
    `<meta property="og:image" content="${OG_IMAGE}">`,
    `<meta name="twitter:card" content="summary_large_image">`,
    `<script type="application/ld+json">${jsonLd}</script>`,
  ].join('\n    ');
};

const setMeta = (selector, content) => {
  const element = document.head.querySelector(selector);
  if (element) element.setAttribute('content', content);
};

// Updates title, description and canonical on client-side route changes so
// they match the prerendered HTML of the route being shown.
export const usePageMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Servers may redirect /convertidor to /convertidor/ (directory index).
    const normalized = pathname.replace(/\/+$/, '') || '/';
    // Unknown paths keep whatever tags the served HTML already has.
    if (!PAGES[normalized]) return;
    const path = normalized;
    const page = PAGES[path];
    const url = `${SITE_URL}${path === '/' ? '/' : path}`;

    document.title = page.title;
    setMeta('meta[name="description"]', page.description);
    setMeta('meta[property="og:title"]', page.title);
    setMeta('meta[property="og:description"]', page.description);
    setMeta('meta[property="og:url"]', url);
    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', url);
  }, [pathname]);
};
